import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import HelpOutlineOutlinedIcon from "@mui/icons-material/HelpOutlineOutlined";
import {
  Autocomplete,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  FormControlLabel,
  Stack,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import React, { useEffect, useState } from "react";
import { ChangeEventType, FormErrors, FormValues } from "../AssignmentUploads";
import { usePortal } from "../../../../context/PortalsContext";
import { brand, gray } from "../../../../theme/themePrimitives";
import { MAX_LENGTH_TITLE } from "../../../../utils/inputLimits";
import {
  AssignmentDependency,
  DdlAssignment,
  PackageType,
} from "../../../../services/api/types/assignments/assignment";
import { TranslateFieldModal } from "./TranslateFieldModal";
import { ReactComponent as TranslateIcon } from "../../../../assets/translate.svg";

interface ClientAdminFormProps {
  formValues: FormValues;
  errors: FormErrors;
  handleChange: (e: ChangeEventType) => void;
  handleSave: () => void;
  buttonLoading: boolean;
  isEditMode: boolean;
  setFormValues: React.Dispatch<React.SetStateAction<FormValues>>;
  portalOwner: string | null;
  dependencyOptions: DdlAssignment[];
}

export const ClientAdminForm: React.FC<ClientAdminFormProps> = ({
  formValues,
  errors,
  handleChange,
  handleSave,
  buttonLoading,
  isEditMode,
  setFormValues,
  portalOwner,
  dependencyOptions,
}) => {
  const { portals } = usePortal();
  const [showSubPortalsToggle, setShowSubportalsToggle] = useState(false);
  const [titleModalOpen, setTitleModalOpen] = useState(false);
  const [titleShortModalOpen, setTitleShortModalOpen] = useState(false);

  const titleTranslations = Object.fromEntries(
    formValues.titleTranslation.map((t) => [t.languageCode, t.title]),
  );
  const titleShortTranslations = Object.fromEntries(
    formValues.titleShortTranslation.map((t) => [t.languageCode, t.titleShort]),
  );

  const handleSaveTitleTranslations = (translations: Record<string, string>) =>
    setFormValues((prev) => ({
      ...prev,
      titleTranslation: Object.entries(translations)
        .filter(([code, title]) => code !== "en" && title.trim() !== "")
        .map(([languageCode, title]) => ({ languageCode, title })),
    }));

  const handleSaveTitleShortTranslations = (
    translations: Record<string, string>,
  ) =>
    setFormValues((prev) => ({
      ...prev,
      titleShortTranslation: Object.entries(translations)
        .filter(
          ([code, titleShort]) => code !== "en" && titleShort.trim() !== "",
        )
        .map(([languageCode, titleShort]) => ({ languageCode, titleShort })),
    }));

  useEffect(() => {
    if (portals.selectedPortal && portalOwner) {
      setShowSubportalsToggle(
        !portals.selectedPortal.isMain &&
          !portals.selectedPortal.isSubPortal &&
          portals.selectedPortal.portalUid === portalOwner,
      );
    }
  }, [portals.selectedPortal, portalOwner]);

  return (
    <Stack spacing={2}>
      <Stack spacing={2} width={"100%"}>
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
            columnGap: 4,
            rowGap: 2,
          }}
        >
          <Stack spacing={1}>
            <Box
              display="flex"
              alignItems="center"
              justifyContent="space-between"
            >
              <Typography variant="subtitle2">
                Primary title
                <Box component="span" sx={{ color: `var(--error-700)` }}>
                  *
                </Box>
              </Typography>
              <TranslateIcon
                onClick={() =>
                  formValues.title.trim() !== "" && setTitleModalOpen(true)
                }
                style={{
                  width: 22,
                  height: 22,
                  borderRadius: "2px",
                  borderRight:
                    formValues.title.trim() !== ""
                      ? `1px solid ${brand[600]}`
                      : "1px solid #98A2B3",
                  borderBottom:
                    formValues.title.trim() !== ""
                      ? `1px solid ${brand[600]}`
                      : "1px solid #98A2B3",
                  cursor:
                    formValues.title.trim() !== "" ? "pointer" : "default",
                  color:
                    formValues.title.trim() !== "" ? brand[600] : "#98A2B3",
                }}
              />
            </Box>
            <TextField
              fullWidth
              id="title"
              name="title"
              placeholder="Enter the title in English first"
              type="text"
              value={formValues.title}
              onChange={handleChange}
              variant="outlined"
              size="small"
              error={Boolean(errors.title)}
              helperText={errors.title}
              slotProps={{
                input: {
                  endAdornment: errors.title && (
                    <ErrorOutlineIcon color="error" fontSize="small" />
                  ),
                },
                htmlInput: { maxLength: MAX_LENGTH_TITLE },
              }}
            />
          </Stack>
          <Stack spacing={1}>
            <Box
              display="flex"
              alignItems="center"
              justifyContent="space-between"
            >
              <Typography variant="subtitle2">Thumbnail title</Typography>
              <TranslateIcon
                onClick={() =>
                  formValues.titleShort.trim() !== "" &&
                  setTitleShortModalOpen(true)
                }
                style={{
                  width: 22,
                  height: 22,
                  borderRadius: "2px",
                  borderRight:
                    formValues.titleShort.trim() !== ""
                      ? `1px solid ${brand[600]}`
                      : "1px solid #98A2B3",
                  borderBottom:
                    formValues.titleShort.trim() !== ""
                      ? `1px solid ${brand[600]}`
                      : "1px solid #98A2B3",
                  cursor:
                    formValues.titleShort.trim() !== "" ? "pointer" : "default",
                  color:
                    formValues.titleShort.trim() !== ""
                      ? brand[600]
                      : "#98A2B3",
                }}
              />
            </Box>
            <TextField
              fullWidth
              id="titleShort"
              name="titleShort"
              placeholder="Enter the title in English first"
              type="text"
              value={formValues.titleShort}
              onChange={handleChange}
              variant="outlined"
              size="small"
              error={Boolean(errors.titleShort)}
              helperText={errors.titleShort}
              slotProps={{
                input: {
                  endAdornment: errors.titleShort ? (
                    <ErrorOutlineIcon color="error" fontSize="small" />
                  ) : (
                    <Tooltip
                      title="Displayed on assignment cards. Defaults to Primary Title if left empty."
                      arrow
                      placement="right"
                      slotProps={{
                        tooltip: {
                          sx: {
                            backgroundColor: gray[800],
                          },
                        },
                        arrow: {
                          sx: {
                            color: gray[800],
                          },
                        },
                      }}
                    >
                      <HelpOutlineOutlinedIcon
                        sx={{
                          fontSize: 16,
                          color: "#98A2B3",
                          cursor: "pointer",
                        }}
                      />
                    </Tooltip>
                  ),
                },
                htmlInput: { maxLength: MAX_LENGTH_TITLE },
              }}
            />
          </Stack>
          <Stack spacing={1}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
              <Typography variant="subtitle2">Dependency</Typography>
              <Tooltip
                title="Defines assignment dependencies."
                arrow
                placement="right"
                slotProps={{
                  tooltip: {
                    sx: {
                      backgroundColor: gray[800],
                    },
                  },
                  arrow: {
                    sx: {
                      color: gray[800],
                    },
                  },
                }}
              >
                <HelpOutlineOutlinedIcon
                  sx={{
                    fontSize: 16,
                    color: "#98A2B3",
                    cursor: "pointer",
                  }}
                />
              </Tooltip>
            </Box>
            <Autocomplete<DdlAssignment, true>
              id="assignment-upload-dependency-autocomplete"
              multiple
              filterSelectedOptions
              options={dependencyOptions}
              value={dependencyOptions.filter((o) =>
                formValues.dependency.some(
                  (d) => d.prerequisiteAssignmentUid === o.AssignmentUid,
                ),
              )}
              size="small"
              isOptionEqualToValue={(a, b) =>
                a.AssignmentUid === b.AssignmentUid
              }
              getOptionLabel={(o) => o.title}
              onChange={(_, newValue) =>
                setFormValues((prev) => ({
                  ...prev,
                  dependency: newValue.map(
                    (o): AssignmentDependency => ({
                      prerequisiteAssignmentUid: o.AssignmentUid,
                      prerequisiteAssignmentId: o.AssignmentId,
                    }),
                  ),
                }))
              }
              renderInput={(params) => (
                <TextField
                  {...params}
                  placeholder="Select assignments"
                  error={Boolean(errors.dependency)}
                  helperText={errors.dependency}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      minHeight: "44px",
                    },
                  }}
                />
              )}
              renderValue={(value, getTagProps) => (
                <Box
                  component="span"
                  sx={{ display: "inline-flex", flexWrap: "wrap", gap: 1 }}
                >
                  {value.map((option, index) => {
                    const { key, ...rest } = getTagProps({ index });
                    return (
                      <Chip
                        key={key}
                        {...rest}
                        label={option.title}
                        sx={{
                          backgroundColor: "white",
                          padding: "14px 6px",
                          borderRadius: "6px",
                        }}
                      />
                    );
                  })}
                </Box>
              )}
              slotProps={{
                popupIndicator: { sx: { border: "none" } },
                clearIndicator: { sx: { display: "none" } },
              }}
            />
          </Stack>
          <Stack spacing={1}>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                height: "22px",
              }}
            >
              <Typography
                variant="subtitle2"
                sx={{
                  color: `${formValues.packageType === PackageType.PdfUpload || formValues.packageType === PackageType.VideoUpload ? gray[400] : "black"}`,
                }}
              >
                Max no. attempts
              </Typography>
              <FormControlLabel
                sx={{ mr: 0, ml: 0, height: "22px" }}
                slotProps={{ typography: { variant: "subtitle2" } }}
                control={
                  <Checkbox
                    size="small"
                    sx={{ p: "2px" }}
                    checked={formValues.attemptsUnlimited}
                    disabled={
                      formValues.packageType === PackageType.PdfUpload ||
                      formValues.packageType === PackageType.VideoUpload
                    }
                    onChange={(e) => {
                      const checked = e.target.checked;
                      setFormValues((prev) => ({
                        ...prev,
                        attemptsUnlimited: checked,
                        maxAttempts: checked ? undefined : prev.maxAttempts,
                      }));
                    }}
                  />
                }
                label="Unlimited"
              />
            </Box>
            <TextField
              placeholder="Select no. attempts"
              type="number"
              value={
                formValues.attemptsUnlimited
                  ? ""
                  : (formValues.maxAttempts ?? "")
              }
              disabled={
                formValues.packageType === PackageType.PdfUpload ||
                formValues.packageType === PackageType.VideoUpload ||
                formValues.attemptsUnlimited
              }
              onChange={(e) => {
                const sanitized = e.target.value.replace(/[^0-9]/g, "");
                setFormValues((prev) => ({
                  ...prev,
                  maxAttempts: sanitized === "" ? undefined : Number(sanitized),
                }));
              }}
              onKeyDown={(e) =>
                ["-", "+", "e", "E", "."].includes(e.key) && e.preventDefault()
              }
              error={Boolean(errors.maxAttempts)}
              helperText={errors.maxAttempts}
              slotProps={{ htmlInput: { min: 1 } }}
              size="small"
              sx={{
                "& .MuiInputBase-input": {
                  px: "14px",
                  py: "10px",
                  cursor: `${formValues.packageType === PackageType.PdfUpload || formValues.packageType === PackageType.VideoUpload ? "not-allowed" : "text"}`,
                },
                "& .MuiOutlinedInput-root": {
                  height: "44px",
                },
              }}
            />
          </Stack>
        </Box>
        {showSubPortalsToggle && (
          <Box sx={{ display: "flex", gap: "1rem", alignItems: "center" }}>
            <FormControlLabel
              control={
                <Switch
                  checked={formValues.isAvailableToSubportals}
                  onChange={(e) =>
                    setFormValues((prev) => ({
                      ...prev,
                      isAvailableToSubportals: e.target.checked,
                    }))
                  }
                />
              }
              label="Available to subportals"
              sx={{
                marginLeft: 0,
                gap: "1rem",
                "& .MuiFormControlLabel-label": {
                  color: "black",
                },
              }}
            />
          </Box>
        )}
        {/* <Stack spacing={1}>
          <Typography variant="subtitle2" color={primaryTextColor}>
            Status
          </Typography>
          <Select
            disabled={isEditMode && formValues.status === undefined}
            name="status"
            IconComponent={ExpandMoreIcon}
            value={formValues.status || ""}
            error={Boolean(errors.status)}
            onChange={handleChange}
            sx={{
              borderRadius: "12px",
              minWidth: "150px",
              backgroundColor: "transparent",
              "&:hover": {
                backgroundColor: "transparent",
              },
              "& .MuiSelect-select": {
                display: "flex",
                alignItems: "center",
                padding: "8.5px 14px",
              },
            }}
            size="medium"
          >
            <MenuItem value={Status.ACTIVE}>Active</MenuItem>
            <MenuItem value={Status.INACTIVE}>Inactive</MenuItem>
          </Select>
          {errors.status && (
            <Typography
              sx={{ margin: "4px 4px 0px !important" }}
              fontSize={"12px"}
              color={error[600]}
            >
              {errors.status}
            </Typography>
          )}
        </Stack> */}
      </Stack>
      <Box mt={2} sx={{ display: "flex", justifyContent: "center" }}>
        {!isEditMode && (
          <>
            {buttonLoading ? (
              <CircularProgress />
            ) : (
              <Button
                onClick={handleSave}
                sx={{
                  px: 2,
                  borderRadius: "12px",
                  backgroundColor: `${brand[600]} !important`,
                  color: `white`,
                }}
              >
                Continue
              </Button>
            )}
          </>
        )}
      </Box>
      <TranslateFieldModal
        open={titleModalOpen}
        onClose={() => setTitleModalOpen(false)}
        fieldLabel="Primary title"
        englishValue={formValues.title}
        onEnglishValueChange={(value) =>
          handleChange({
            target: { name: "title", value },
          } as ChangeEventType)
        }
        translations={titleTranslations}
        onSave={handleSaveTitleTranslations}
      />
      <TranslateFieldModal
        open={titleShortModalOpen}
        onClose={() => setTitleShortModalOpen(false)}
        fieldLabel="Thumbnail title"
        englishValue={formValues.titleShort}
        onEnglishValueChange={(value) =>
          handleChange({
            target: { name: "titleShort", value },
          } as ChangeEventType)
        }
        translations={titleShortTranslations}
        onSave={handleSaveTitleShortTranslations}
      />
    </Stack>
  );
};
